import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, MapPin, Calendar, Users, Loader2, CheckCircle,
  ChevronRight, Save, Bus, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_lunch-roster-hub/artifacts/wio7p25o_rr%20logo.png';

// Merge CIT subgroups into one "CIT" label for display
const mergeGroupDisplay = (groups) => {
  const nonCit = groups.filter(g => !g.toUpperCase().startsWith('CIT'));
  const hasCit = groups.some(g => g.toUpperCase().startsWith('CIT'));
  return hasCit ? [...nonCit, 'CIT'] : nonCit;
};

// Trip list view
const TripList = ({ trips, onSelectTrip, onBack }) => {
  // Group trips by date
  const tripsByDate = {};
  trips.forEach(t => {
    if (!tripsByDate[t.date]) tripsByDate[t.date] = [];
    tripsByDate[t.date].push(t);
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDB813]/10 to-white">
      <div className="max-w-4xl mx-auto p-4 lg:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} data-testid="trips-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="RRDC" className="w-10 h-10 rounded-full" />
              <div>
                <h1 className="text-2xl font-black text-[#2B4F8C]">Trip Lunch Orders</h1>
                <p className="text-sm text-slate-500">Select a trip to place advance lunch orders</p>
              </div>
            </div>
          </div>
          <a
            href="https://campersnapshot.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-slate-500 hover:text-[#2B4F8C] px-3 py-2 rounded-lg border border-slate-200 hover:border-[#2B4F8C] transition-colors"
            data-testid="campersnapshot-link"
          >
            Back to CamperSnapshot
          </a>
        </div>

        <div className="space-y-6">
          {Object.entries(tripsByDate).map(([date, dateTrips]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#2B4F8C]" />
                <h2 className="text-lg font-bold text-slate-800">{formatDate(date)}</h2>
              </div>
              <div className="grid gap-3">
                {dateTrips.map(trip => {
                  const isLocked = !trip.ordering_open;
                  const isPast = trip.is_past;
                  return (
                  <Card
                    key={trip.id}
                    className={`transition-all ${isLocked || isPast ? 'opacity-60 cursor-default' : 'cursor-pointer hover:shadow-md hover:border-[#2B4F8C]/30'}`}
                    onClick={() => !isLocked && !isPast && onSelectTrip(trip)}
                    data-testid={`trip-card-${trip.id}`}
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLocked || isPast ? 'bg-slate-200' : 'bg-[#2B4F8C]/10'}`}>
                          {isLocked && !isPast ? (
                            <Lock className="w-5 h-5 text-slate-400" />
                          ) : (
                            <Bus className={`w-5 h-5 ${isPast ? 'text-slate-400' : 'text-[#2B4F8C]'}`} />
                          )}
                        </div>
                        <div>
                          <h3 className={`font-bold ${isPast ? 'text-slate-400' : 'text-slate-900'}`}>{trip.trip_name}</h3>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {mergeGroupDisplay(trip.groups).map(g => (
                              <Badge key={g} variant="outline" className="text-xs">{g}</Badge>
                            ))}
                          </div>
                          {isLocked && !isPast && trip.opens_date && (
                            <p className="text-xs text-amber-600 mt-1 font-medium" data-testid={`trip-opens-${trip.id}`}>
                              {trip.days_until_open > 0 
                                ? `Opens ${new Date(trip.opens_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                : `Closed ${new Date(trip.closes_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                              }
                            </p>
                          )}
                          {isPast && (
                            <p className="text-xs text-slate-400 mt-1">Trip completed</p>
                          )}
                        </div>
                      </div>
                      {!isLocked && !isPast ? (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      ) : (
                        <Badge variant="outline" className={`text-xs ${isPast ? 'text-slate-400 border-slate-300' : 'text-amber-600 border-amber-300'}`}>
                          {isPast ? 'Past' : 'Locked'}
                        </Badge>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {trips.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Bus className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No trips scheduled</p>
          </div>
        )}
      </div>
    </div>
  );
};

const SUB_LUNCH_OPTIONS = [
  "Cheese/Turkey",
  "Cheese/Ham",
  "Cheese/Salami",
  "Cheese/Bologna",
  "Bologna/Cheese",
  "Bologna",
  "Salami",
  "Turkey",
  "Ham",
  "Butter Bagel",
  "Cream Cheese Bagel",
  "Plain Bagel"
];

// Trip order form
const TripOrderForm = ({ trip, onBack }) => {
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [campers, setCampers] = useState([]);
  const [orders, setOrders] = useState({});
  const [savedOrders, setSavedOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);

  // Search for matching groups
  const searchGroups = useCallback(async () => {
    if (!groupSearch.trim()) return;
    setLoading(true);
    try {
      const res = await api.getGroups();
      const allGroups = res.data || [];
      const query = groupSearch.toLowerCase().replace(/^0+/, '');
      const matched = allGroups.filter(g => {
        const name = g.name.toLowerCase();
        const code = (g.code || '').toLowerCase().replace(/^0+/, '');
        return name.includes(query) || code.includes(query) ||
               name.replace(/^[bg]0*/, '').startsWith(query.replace(/^[bg]0*/, ''));
      });
      setGroups(matched);
    } catch { toast.error('Failed to search groups'); }
    finally { setLoading(false); }
  }, [groupSearch]);

  useEffect(() => {
    if (groupSearch.length >= 1) {
      const t = setTimeout(searchGroups, 300);
      return () => clearTimeout(t);
    } else {
      setGroups([]);
    }
  }, [groupSearch, searchGroups]);

  // Load campers when group selected
  const handleSelectGroup = async (group) => {
    setSelectedGroup(group);
    setGroups([]);
    setLoading(true);
    try {
      const [campersRes, ordersRes] = await Promise.all([
        api.getCampersByGroup(group.id),
        api.getTripOrders(trip.id)
      ]);
      setCampers(campersRes.data || []);
      // Pre-fill existing orders
      const existing = {};
      const saved = {};
      (ordersRes.data || []).forEach(o => {
        existing[o.camper_id] = o.lunch_choice;
        saved[o.camper_id] = true;
      });
      setOrders(existing);
      setSavedOrders(saved);
    } catch { toast.error('Failed to load campers'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    const orderList = campers
      .filter(c => orders[c.id] && orders[c.id] !== 'Sub Lunch' && orders[c.id] !== 'Trip Lunch') // skip incomplete two-step picks
      .map(c => ({
        camper_id: c.id,
        camper_name: `${c.preferred_name || c.first_name} ${c.last_name}`,
        is_staff: c.is_staff || false,
        lunch_choice: orders[c.id]
      }));

    if (orderList.length === 0) {
      toast.error('Please select at least one lunch option');
      return;
    }

    setSaving(true);
    try {
      await api.submitTripOrder(trip.id, {
        orders: orderList,
        group_name: selectedGroup.name
      });
      toast.success(`Saved ${orderList.length} orders for ${trip.trip_name}!`);
      const newSaved = {};
      orderList.forEach(o => { newSaved[o.camper_id] = true; });
      setSavedOrders(prev => ({ ...prev, ...newSaved }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save orders'); }
    finally { setSaving(false); }
  };

  const completedCount = Object.keys(orders).filter(k => {
    const val = orders[k];
    if (!val) return false;
    if (val === 'Sub Lunch') return false; // needs sub-type
    if (val === 'Trip Lunch') return false; // needs trip lunch item
    return true;
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDB813]/10 to-white">
      <div className="max-w-4xl mx-auto p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" onClick={onBack} data-testid="trip-order-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-black text-[#2B4F8C]">{trip.trip_name}</h1>
            <p className="text-sm text-slate-500">
              {new Date(trip.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              {' '}&middot; Groups: {mergeGroupDisplay(trip.groups).join(', ')}
            </p>
          </div>
        </div>

        {/* Group search (if not selected) */}
        {!selectedGroup && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Select Your Group</CardTitle>
            </CardHeader>
            <CardContent>
              <Input
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                placeholder="Enter group name or number..."
                data-testid="trip-group-search"
                autoFocus
              />
              {loading && <Loader2 className="w-5 h-5 animate-spin mx-auto mt-4 text-[#2B4F8C]" />}
              {groups.length > 0 && (
                <div className="mt-3 space-y-2">
                  {groups.map(g => (
                    <Button
                      key={g.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handleSelectGroup(g)}
                      data-testid={`trip-group-option-${g.id}`}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {g.name}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Order form */}
        {selectedGroup && (
          <>
            <Card className="mb-4">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#2B4F8C]" />
                  <span className="font-bold">{selectedGroup.name}</span>
                  <Badge variant="outline">{completedCount} of {campers.length} ordered</Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSelectedGroup(null); setCampers([]); setOrders({}); setSavedOrders({}); }}>
                  Change Group
                </Button>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
              </div>
            ) : (
              <div className="space-y-3">
                {campers.map(camper => {
                  const currentOrder = orders[camper.id] || '';
                  const isSubLunch = currentOrder === 'Sub Lunch' || currentOrder.startsWith('Sub Lunch - ');
                  const subChoice = currentOrder.startsWith('Sub Lunch - ') ? currentOrder.replace('Sub Lunch - ', '') : '';
                  const isTripLunch = currentOrder === 'Trip Lunch' || currentOrder.startsWith('Trip Lunch - ');
                  const tripLunchChoice = currentOrder.startsWith('Trip Lunch - ') ? currentOrder.replace('Trip Lunch - ', '') : '';
                  const mainChoice = currentOrder.startsWith('Sub Lunch') ? 'Sub Lunch'
                    : currentOrder.startsWith('Trip Lunch') ? 'Trip Lunch'
                    : currentOrder;

                  return (
                    <Card key={camper.id} className={`transition-all ${savedOrders[camper.id] ? 'border-green-300 bg-green-50/50' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-bold text-slate-900 truncate">
                              {camper.preferred_name || camper.first_name} {camper.last_name}
                            </span>
                            {camper.is_staff && (
                              <Badge className="bg-[#2B4F8C] text-white text-xs shrink-0">Staff</Badge>
                            )}
                            {savedOrders[camper.id] && (
                              <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                            )}
                          </div>
                          <Select
                            value={mainChoice}
                            onValueChange={(val) => {
                              if (val === 'Sub Lunch') {
                                setOrders(prev => ({ ...prev, [camper.id]: 'Sub Lunch' }));
                              } else {
                                setOrders(prev => ({ ...prev, [camper.id]: val }));
                              }
                            }}
                          >
                            <SelectTrigger className="w-[220px]" data-testid={`trip-lunch-select-${camper.id}`}>
                              <SelectValue placeholder="Select lunch..." />
                            </SelectTrigger>
                            <SelectContent>
                              {trip.lunch_options.map(opt => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Sub Lunch type picker */}
                        {isSubLunch && (
                          <div className="mt-3 ml-0 pl-4 border-l-2 border-[#FDB813]">
                            <p className="text-xs font-semibold text-slate-500 mb-2">Select sub type:</p>
                            <Select
                              value={subChoice}
                              onValueChange={(val) => setOrders(prev => ({ ...prev, [camper.id]: `Sub Lunch - ${val}` }))}
                            >
                              <SelectTrigger className="w-full" data-testid={`trip-sub-select-${camper.id}`}>
                                <SelectValue placeholder="Choose sub type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {SUB_LUNCH_OPTIONS.map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {/* Trip Lunch item picker (Adventureland) */}
                        {isTripLunch && trip.venue_lunch_options?.length > 0 && (
                          <div className="mt-3 ml-0 pl-4 border-l-2 border-[#FDB813]">
                            <p className="text-xs font-semibold text-slate-500 mb-2">Select trip lunch:</p>
                            <Select
                              value={tripLunchChoice}
                              onValueChange={(val) => setOrders(prev => ({ ...prev, [camper.id]: `Trip Lunch - ${val}` }))}
                            >
                              <SelectTrigger className="w-full" data-testid={`trip-venue-select-${camper.id}`}>
                                <SelectValue placeholder="Choose hot dog, hamburger, or chicken tenders..." />
                              </SelectTrigger>
                              <SelectContent>
                                {trip.venue_lunch_options.map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}

                {campers.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <p>No campers found in this group</p>
                  </div>
                )}

                {campers.length > 0 && (
                  <Button
                    onClick={handleSubmit}
                    disabled={saving || completedCount === 0}
                    className="w-full bg-[#2B4F8C] hover:bg-[#1E3A6B] text-white h-14 text-lg mt-4"
                    data-testid="trip-submit-orders-btn"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
                    Save Trip Orders ({completedCount})
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Main Trips Page
export default function TripsPage() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const res = await api.getTrips();
        setTrips(res.data || []);
      } catch { toast.error('Failed to load trips'); }
      finally { setLoading(false); }
    };
    fetchTrips();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2B4F8C]" />
      </div>
    );
  }

  if (selectedTrip) {
    return <TripOrderForm trip={selectedTrip} onBack={() => setSelectedTrip(null)} />;
  }

  return <TripList trips={trips} onSelectTrip={setSelectedTrip} onBack={() => navigate('/')} />;
}
